import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Controller } from "react-hook-form";
import styles from "./RichTextEditor.module.scss";
import { combineClasses } from "@/lib/utils";
import withLabelAndError from "../withLabelAndError";
import BoldIcon from "@/components/SVGComponents/BoldIcon";
import UnderlineIcon from "@/components/SVGComponents/UnderlineIcon";
import ItalicIcon from "@/components/SVGComponents/ItalicIcon";
import LinkIcon from "@/components/SVGComponents/LinkIcon";
import ListIcon from "@/components/SVGComponents/ListIcon";
import CrossIcon from "@/assets/svg/LoginPage/CrossIcon";

interface Props {
  name: string;
  control: any;
  validations?: any;
  placeholder?: string;
  error?: string;
  className?: string;
}

const EditorUI = ({ field, placeholder, error, className }: any) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({
        placeholder: placeholder || "Write something...",
      }),
    ],
    content: field.value || "",
    onUpdate: ({ editor }) => {
      field.onChange(editor.getHTML());
    },
  });

  if (!editor) return null;

  const setLink = () => {
    const url = prompt("Enter URL");
    if (!url) return;
    editor.chain().focus().setLink({ href: url }).run();
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
  };

  return (
    <div
      className={combineClasses(
        styles.editorWrapper,
        error && styles.error,
        className,
      )}
    >
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive("bold") ? styles.active : ""}
        >
          <BoldIcon strokeWidth={editor.isActive("bold") ? 2 : 1.5} />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive("italic") ? styles.active : ""}
        >
          <ItalicIcon strokeWidth={editor.isActive("italic") ? 2 : 1.5} />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive("underline") ? styles.active : ""}
        >
          <UnderlineIcon strokeWidth={editor.isActive("underline") ? 2 : 1.5} />
        </button>

        <div className={styles.divider} />

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive("bulletList") ? styles.active : ""}
        >
          <ListIcon strokeWidth={editor.isActive("bulletList") ? 2 : 1.5} />
        </button>

        <div className={styles.divider} />

        <button type="button" onClick={setLink}>
          <LinkIcon />
        </button>
        <button type="button" onClick={removeLink}>
          <CrossIcon />
        </button>
      </div>

      <EditorContent
        onClick={() => editor?.chain().focus().run()}
        editor={editor}
        className={styles.editor}
      />
    </div>
  );
};

const RichTextEditor: React.FC<Props> = ({
  name,
  control,
  validations,
  placeholder,
  error,
  className,
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={validations}
      render={({ field }) => (
        <EditorUI
          field={field}
          placeholder={placeholder}
          error={error}
          className={className}
        />
      )}
    />
  );
};

const RichTextWithLabelAndError = withLabelAndError(RichTextEditor as any);

export default RichTextWithLabelAndError;
export { RichTextEditor };
